/** Structured “Details” captured on CRM deals; copied to projects as JSON on conversion. */

export interface LeadRequirementDetails {
  site: {
    plotSize: string;
    roadFacingDirection: string;
    buildingHeightFromRoad: string;
    localRegulationsSetback: string;
  };
  clientProfile: {
    familyMembers: string;
    ageGroup: string;
    workFromHome: string;
    dailyRoutine: string;
    privacyExpectations: string;
  };
  space: {
    generalNeeds: string;
  };
  floorPlanning: {
    numberOfFloors: string;
    duplexRentalSelfUse: string;
    futureExpansion: string;
    terraceUsage: string;
    liftRequirement: string;
  };
  preferences: {
    vastu: string;
    vastuDirections: string;
    designStyle: string;
    elevationExpectations: string;
    balconySitout: string;
  };
}

const EMPTY: LeadRequirementDetails = {
  site: {
    plotSize: '',
    roadFacingDirection: '',
    buildingHeightFromRoad: '',
    localRegulationsSetback: '',
  },
  clientProfile: {
    familyMembers: '',
    ageGroup: '',
    workFromHome: '',
    dailyRoutine: '',
    privacyExpectations: '',
  },
  space: {
    generalNeeds: '',
  },
  floorPlanning: {
    numberOfFloors: '',
    duplexRentalSelfUse: '',
    futureExpansion: '',
    terraceUsage: '',
    liftRequirement: '',
  },
  preferences: {
    vastu: '',
    vastuDirections: '',
    designStyle: '',
    elevationExpectations: '',
    balconySitout: '',
  },
};

export function emptyLeadRequirementDetails(): LeadRequirementDetails {
  return JSON.parse(JSON.stringify(EMPTY)) as LeadRequirementDetails;
}

function isNonEmpty(s: unknown): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

/** True if any field has non-whitespace content. */
export function leadRequirementDetailsHasContent(d: LeadRequirementDetails): boolean {
  const walk = (v: unknown): boolean => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return isNonEmpty(v);
    if (typeof v === 'object' && !Array.isArray(v)) {
      return Object.values(v as Record<string, unknown>).some(walk);
    }
    return false;
  };
  return walk(d);
}

/** For DB: `{}` and null text when nothing filled. */
export function pruneLeadRequirementDetailsForStorage(
  d: LeadRequirementDetails
): { json: Record<string, unknown>; text: string | null } {
  if (!leadRequirementDetailsHasContent(d)) {
    return { json: {}, text: null };
  }
  return {
    json: d as unknown as Record<string, unknown>,
    text: formatLeadRequirementDetailsAsPlainText(d),
  };
}

function line(label: string, value: string | undefined): string | null {
  const v = value?.trim();
  if (!v) return null;
  return `${label}: ${v}`;
}

/**
 * Plain-text block for leads.requirement_details_text and lead detail display.
 * Keep in sync with section labels shown in the form.
 */
export function formatLeadRequirementDetailsAsPlainText(d: LeadRequirementDetails): string {
  const blocks: string[] = [];

  const siteLines = [
    line('Plot size', d.site?.plotSize),
    line('Road facing and direction', d.site?.roadFacingDirection),
    line('Building height from road level', d.site?.buildingHeightFromRoad),
    line('Local regulations / setbacks', d.site?.localRegulationsSetback),
  ].filter(Boolean) as string[];
  if (siteLines.length) blocks.push(['Site information', ...siteLines].join('\n'));

  const clientLines = [
    line('Number of family members', d.clientProfile?.familyMembers),
    line('Age group', d.clientProfile?.ageGroup),
    line('Work-from-home requirements', d.clientProfile?.workFromHome),
    line('Daily routine', d.clientProfile?.dailyRoutine),
    line('Privacy expectations', d.clientProfile?.privacyExpectations),
  ].filter(Boolean) as string[];
  if (clientLines.length) blocks.push(['Client profile & lifestyle', ...clientLines].join('\n'));

  const spaceL = line('General space needs', d.space?.generalNeeds);
  if (spaceL) blocks.push(['Space requirements', spaceL].join('\n'));

  const fpLines = [
    line('Number of floors', d.floorPlanning?.numberOfFloors),
    line('Duplex / rental / self-use', d.floorPlanning?.duplexRentalSelfUse),
    line('Future expansion plans', d.floorPlanning?.futureExpansion),
    line('Terrace usage', d.floorPlanning?.terraceUsage),
    line('Lift requirement', d.floorPlanning?.liftRequirement),
  ].filter(Boolean) as string[];
  if (fpLines.length) blocks.push(['Floor planning', ...fpLines].join('\n'));

  const prefBody: string[] = [];
  const v = d.preferences?.vastu?.trim();
  if (v === 'yes' || v === 'no') {
    prefBody.push(`Vastu required: ${v}`);
  } else if (v) {
    prefBody.push(`Vastu: ${v}`);
  }
  const dir = d.preferences?.vastuDirections?.trim();
  if (dir) prefBody.push(`Preferred directions (Vastu): ${dir}`);
  const style = d.preferences?.designStyle?.trim();
  if (style) prefBody.push(`Design / aesthetic style: ${style}`);
  const elev = d.preferences?.elevationExpectations?.trim();
  if (elev) prefBody.push(`Elevation expectations: ${elev}`);
  const bal = d.preferences?.balconySitout?.trim();
  if (bal) prefBody.push(`Balcony and sit-out preferences: ${bal}`);
  if (prefBody.length) blocks.push(['Preferences', ...prefBody].join('\n'));

  return blocks.join('\n\n');
}

/** Normalize API/DB JSON into a full form shape. */
export function parseLeadRequirementDetails(raw: unknown): LeadRequirementDetails {
  const base = emptyLeadRequirementDetails();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const o = raw as Record<string, unknown>;
  const mergeStr = (s: unknown): string => (typeof s === 'string' ? s : '');
  const mSite = (o.site as Record<string, unknown>) || {};
  const mClient = (o.clientProfile as Record<string, unknown>) || {};
  const mSpace = (o.space as Record<string, unknown>) || {};
  const mFp = (o.floorPlanning as Record<string, unknown>) || {};
  const mPref = (o.preferences as Record<string, unknown>) || {};

  base.site = {
    plotSize: mergeStr(mSite.plotSize),
    roadFacingDirection: mergeStr(mSite.roadFacingDirection),
    buildingHeightFromRoad: mergeStr(mSite.buildingHeightFromRoad),
    localRegulationsSetback: mergeStr(mSite.localRegulationsSetback),
  };
  base.clientProfile = {
    familyMembers: mergeStr(mClient.familyMembers),
    ageGroup: mergeStr(mClient.ageGroup),
    workFromHome: mergeStr(mClient.workFromHome),
    dailyRoutine: mergeStr(mClient.dailyRoutine),
    privacyExpectations: mergeStr(mClient.privacyExpectations),
  };
  base.space = { generalNeeds: mergeStr(mSpace.generalNeeds) };
  base.floorPlanning = {
    numberOfFloors: mergeStr(mFp.numberOfFloors),
    duplexRentalSelfUse: mergeStr(mFp.duplexRentalSelfUse),
    futureExpansion: mergeStr(mFp.futureExpansion),
    terraceUsage: mergeStr(mFp.terraceUsage),
    liftRequirement: mergeStr(mFp.liftRequirement),
  };
  base.preferences = {
    vastu: mergeStr(mPref.vastu),
    vastuDirections: mergeStr(mPref.vastuDirections),
    designStyle: mergeStr(mPref.designStyle),
    elevationExpectations: mergeStr(mPref.elevationExpectations),
    balconySitout: mergeStr(mPref.balconySitout),
  };
  return base;
}
